import {
  isMapboxURL,
  transformMapboxUrl,
} from "maplibregl-mapbox-request-transformer";

/**
 * This is a public access token connected to THE CITY's MapBox account:
 */
export const MAPBOX_TOKEN =
  "pk.eyJ1IjoidGhlLWNpdHkiLCJhIjoiY2xhMWVuaDNqMDZ1ZzNxbzNkM3poMHBheSJ9.SJAnL4rHAR6jShHQniZZHg";

/**
 * This is a link to our custom MapBox Studio basemap style:
 */
export const MAPBOX_STYLE_URL =
  "mapbox://styles/the-city/cla76ue2h001514o67feib2ey";

/**
 * Since our custom style does not include links for map sprites, let's specify a fallback map style to load in:
 */
const FALLBACK_STYLE_URL_ROOT =
  "https://tiles.basemaps.cartocdn.com/gl/positron-gl-style/sprite@2x";

export const transformStyleRequest = (url, resourceType) => {
  if (resourceType === "SpriteImage") {
    return {
      url: `${FALLBACK_STYLE_URL_ROOT}.png`,
    };
  }
  if (resourceType === "SpriteJSON") {
    return {
      url: `${FALLBACK_STYLE_URL_ROOT}.json`,
    };
  }
  if (isMapboxURL(url)) {
    return transformMapboxUrl(url, resourceType, MAPBOX_TOKEN);
  }
  return { url };
};

export const FULL_LOSS_COLOR = "#fff5f0";
export const TWENTY_19_COLOR = "#ffe4d7";
export const TWENTY_20_COLOR = "#fcbba1";
export const TWENTY_21_COLOR = "#fb6a4a";
export const TWENTY_22_COLOR = "#cb181d";
export const TWENTY_23_COLOR = "#67000d";
export const PARTIAL_LOSS_COLOR = "#fff5f0";
export const NEIGHBORHOOD_BOUNDARY_COLOR = "#fff5f0";

const yellowToRedRamp = ['#ffe4d7','#fcbba1','#fb6a4a','#cb181d','#67000d'];

// const yellowToRedRamp = ['#fee0d2','#fcbba1','#fc9272','#fb6a4a','#cb181d'];

export const defaultPointStyle = {
  "circle-color": [
    "match",
    ["to-number", ["get", "Pub_Year"]],
    2019, TWENTY_19_COLOR,
    2020, TWENTY_20_COLOR,
    2021, TWENTY_21_COLOR,
    2022, TWENTY_22_COLOR,
    2023, TWENTY_23_COLOR,
    PARTIAL_LOSS_COLOR
  ],
  "circle-opacity": [
    "interpolate",
    ["linear"],
    ["zoom"],
    // at initial zoom, circle opacity is near 0 to show blur effect
    10,
    ["case", ["==", ["to-number", ["get", "u21"]], 0], 0.8, 0.8],
    // at building-level zoom, circle opacity is near 1
    15,
    ["case", ["==", ["to-number", ["get", "u21"]], 0], 0.8, 0.8],
  ],
  "circle-radius": [
    "interpolate",
    ["linear"],
    ["zoom"],
    // at initial zoom, circles are smaller
    10,
    3,
    // at mid-level zoom, circles are larger
    14,
    10,
    // at building-level zoom, circles are very large
    17,
    15,
  ],
};

export const getPointStyle = () => ({
  id: "buildings",
  type: "circle",
  paint: defaultPointStyle,
});

export const getHoverStyle = () => ({
  id: "articles-highlighted",
  type: "circle",
  source: "master_c_2",
  paint: {
    ...defaultPointStyle,
    "circle-color": [
      "case",
      ["==", ["to-number", ["get", "u21"]], 0],
      "#DCA206",
      "#A04200",
    ],
    "circle-opacity": 1,
  },
});

export const getClickStyle = () => ({
  id: "articles-clicked",
  type: "circle",
  source: "master_c_2",
  paint: {
    ...defaultPointStyle,
    "circle-color": "hsl(43, 88%, 67%)",
    "circle-stroke-color": "hsl(238, 0%, 11%)",
    "circle-stroke-opacity": 1,
    "circle-stroke-width": 2,
  },
});

export const getLineStyle = () => ({
  id: "nta-boundaries",
  type: "line",
  source: "master_n_2",
  paint: {
    "line-width": 1,
    "line-color": NEIGHBORHOOD_BOUNDARY_COLOR,
  },
});

export const getDefaultFillStyle = () => ({
  id: "nta-filled-2",
  type: "fill",
  source: "composite",
  "paint": {
    //"fill-color": "hsl(210, 98%, 32%)",
    "fill-color": "hsl(210, 98%, 32%)",
    "fill-outline-color": "hsl(0, 06%, 36%)",
    "fill-opacity": 0.1
  },
});

const colorStops = [];
for (let i = 0; i <= 200; i += 5) {
  const color = `hsl(${240 - i}, 100%, 50%)`; // Adjust the hue dynamically
  colorStops.push(i, color);
}

export const getFillStyle = () => {
  return {
    id: "nta-filled",
    type: "fill",
    source: "composite",
    paint: {
      "fill-color": [
        "interpolate",
        //["linear"],
        ["exponential", 0.5],
        ["get", "C"], // Use the 'count' property
        0, "#f7fbff",
        10, "#deebf7",  // Lighter blue  #
        20, "#9ecae1",  // Light blue
        30, "#84bcdc",  // Sky blue
        40, "#6baed6",  // Medium blue
        50, "#4292c6",  // Royal blue
        60, "#2171b5",  // Slightly darker blue
        70, "#08519c",  // Dark blue
        80, "#084594",  // Even darker blue
        90, "#08306b",  // Deep blue
        100, "#06295a", // Dark but clearly blue
        160, "#06295a",
        222, "#042354", // Slightly darker
        //120, "#031d4e", // Dark blue, not too close to black
        //130, "#021746", // Dark blue with a hint of light
        //140, "#021340", // Dark blue, distinguishable from black
        //150, "#020f3a", // Dark blue, visibly blue in low light
        //160, "#020c34", // Dark blue, last shade before very dark
        //170, "#02092e", // Very dark blue, but not black
        //180, "#020728", // Darkest blue, still not black
        //190, "#020522", // Maximum darkness while remaining blue
        //200, "#08306b"  // As dark as possible while avoiding black 

      ],
      "fill-outline-color": "hsl(240, 06%, 36%)",
      "fill-opacity": 0.9,
    },
  };
};

export const getBoundaryStyle = () => ({
  id: "nta-line-Boundary",
  type: "line",
  source: "composite",
  "paint": {
    //"fill-color": "hsl(210, 98%, 32%)",
    "fill-color": "hsl(210, 98%, 32%)",
    "fill-outline-color": "hsl(0, 06%, 36%)",
    "fill-opacity": 0.1
  },
});

export const getLowOpacityFillStyleLine = () => ({
  id: "nta-line-Boundary",
  type: "fill",
  source: "composite",
  "paint": {
    //"fill-color": "hsl(210, 98%, 32%)",
    "fill-color": "hsl(12,26.3%,3.7%)",
    //"fill-outline-color": "hsl(0, 06%, 36%)",
    "fill-opacity": 0.3
  },
});



export const getLowOpacityFillStyle = () => ({
  id: "nta-low-opacity-filled",
  type: "line",
  source: "composite",
  "paint": {
    //"fill-color": "hsl(210, 98%, 32%)",
    "line-color": "hsl(210, 98%, 32%)",
    "line-width": 0.7
  },
});

export const getPopupStyle = (showPopup) => ({
  popup: {
    position: 'relative',
    bottom: '10px',
    left: '10px',
    zIndex: 999,
    display: showPopup ? 'block' : 'none',
    maxWidth: '1000px',
    maxHeight: '10000px',
    overflow: 'visible',
    //border: '1px solid black',
    //borderRadius: '5px',
    padding: '10px',
    //backgroundColor: 'white',
    //boxShadow: '0 0 10px rgba(0, 0, 0, 0.2)', // Optional: adds a shadow
  },
});

export const mapSymbology = {
    colors: yellowToRedRamp,
    breaks: ['2019', '2020', '2021', '2022', '2023'],
    label: <>Published in the year:</>,
    labelShort: "Published in the year",
  }

  export const ntaMapSymbology = {
    colors: [  "#f7fbff", "#6baed6", "#08519c", "#081d58", "#030e00" ],
    breaks: ['0', '', '', '', '200'],
    label: <>Coverage Density:</>,
    labelShort: "Coverage Density",
  }




